import { Theme } from "../data/style/Theme";
import Collapse from "@mui/material/Collapse";
import { useNavigate } from "react-router-dom";
import * as routes from "../data/links/routes";
import { useAuth } from "../context/AuthContext";
import { errorTexts } from "../data/assets/texts";
import { getUsersWorkouts } from "../api/workouts";
import useScreenSize from "../context/ScreenContext";
import { useQuery, useMutation } from "@tanstack/react-query";
import BackupTableIcon from "@mui/icons-material/BackupTable";
import { useNotifications } from "../context/NotificationContext";
import { getExerciseCategories, getWorkoutTypes } from "../api/helpers";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  scrollBar,
  AllCenteredColumn,
  SecondaryTitle,
  SpacedRow,
  CenteredRow,
  MainText,
  MainTitle,
} from "../data/style/Style";
import {
  EXERCISE_CATEGORIES_DATA,
  LEVELOPTIONS,
  WORKOUT_TYPES_DATA,
} from "../data/assets/arrays";

const Box = lazy(() => import("@mui/material/Box"));
const Stack = lazy(() => import("@mui/material/Stack"));
const Loader = lazy(() => import("../components/Display/Loader"));
const WodDisplay = lazy(() => import("../components/WodDisplay"));
const NoDataDiv = lazy(() => import("../components/Display/NoDataDiv"));

const UserWorkouts = () => {
  console.log("RENDER UserWorkouts");
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [limit, setLimit] = useState(0);
  const [typeId, setTypeId] = useState(-1);
  const [levelListVisible, setLevelListVisible] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [workouts, setWorkouts] = useState([]);
  const [categoryId, setCategoryId] = useState(-1);
  const { enableNotification } = useNotifications();
  const [levelId, setLevelId] = useState(profile.level);
  const { mobileDevice, size } = useScreenSize();
  let [width] = size;

  const { data: categories, isPending: categoriesPending } = useQuery({
    queryKey: ["exercise", "categories"],
    queryFn: ({ signal }) => getExerciseCategories({ signal, wodable: 1 }),
    staleTime: 12 * 60 * 60 * 1000,
  });

  const { data: types, isPending: isTypesPending } = useQuery({
    queryKey: ["workout", "types"],
    queryFn: ({ signal }) => getWorkoutTypes({ signal }),
  });

  useEffect(() => {
    if (hasMore)
      fetchWorkouts({
        typeId,
        categoryId,
        levelId,
        limit,
      });
  }, [typeId, categoryId, levelId, limit, hasMore]);

  useEffect(() => {
    setHasMore(true);
    setLimit(0);
    setWorkouts([]);
  }, [typeId, categoryId, levelId]);

  const { mutate: fetchWorkouts } = useMutation({
    mutationFn: getUsersWorkouts,
    onSuccess: (response) => {
      if (response.data.workouts.length < 10) {
        setHasMore(false);
      }
      setWorkouts((prev) => [...prev, ...response.data.workouts]);
      loadingRef.current = false;
    },
    onError: (error) => {
      if (error.response.status === 400) {
        enableNotification(error.response.data.message, "error");
        return;
      }
      if (error.response.status === 403) {
        navigate(routes.LOGIN);
        return;
      }
      if (error.response.status >= 500) {
        enableNotification(errorTexts.system_error, "error");
      }
    },
  });

  const loadingRef = useRef(false);

  const handleScroll = useCallback(
    (e) => {
      if (loadingRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const nearBottom = scrollHeight - scrollTop - clientHeight < 300;

      if (nearBottom && hasMore) {
        loadingRef.current = true;
        setLimit((prev) => prev + 10);
      }
    },
    [hasMore],
  );

  let workoutContent = <Loader height={"90%"} />;

  if (workouts.length > 0) {
    workoutContent = (
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: mobileDevice
            ? "repeat(auto-fit, minmax(275px, 1fr))"
            : "repeat(auto-fit, minmax(370px, 1fr))",
          pb: 2,
        }}
      >
        {workouts.map((workoutId) => (
          <Box
            key={workoutId}
            maxWidth={"465px"}
            minHeight={!mobileDevice && "250px"}
          >
            <Suspense>
              <WodDisplay
                hover={true}
                wodId={workoutId}
                setWorkouts={setWorkouts}
              />
            </Suspense>
          </Box>
        ))}
      </Box>
    );
  } else {
    workoutContent = (
      <Suspense>
        <NoDataDiv
          text={"You have no saved workouts."}
          minHeight={"300px"}
          height={"85%"}
          icon={
            <BackupTableIcon
              sx={{
                fontSize: mobileDevice ? "3rem" : "4rem",
                color: Theme.palette.orange,
              }}
            />
          }
        />
      </Suspense>
    );
  }

  let handleLevelChange = (lvl) => {
    setLevelId(lvl);
    setLevelListVisible(false);
  };

  return (
    <Stack
      gap={2}
      pr={1}
      height={"100%"}
      overflow={"auto"}
      onScroll={handleScroll}
      sx={scrollBar}
    >
      <MainTitle>Saved Workouts</MainTitle>
      <SpacedRow alignItems={"center"} width={"100%"} gap={2} flexWrap={"wrap"}>
        <CenteredRow
          width={width < 600 ? "50%" : "15%"}
          sx={{ cursor: "pointer" }}
          onClick={() => setLevelListVisible((prev) => !prev)}
          gap={1}
          flexWrap={"nowrap"}
          pr={2}
        >
          <KeyboardDoubleArrowUpIcon
            sx={{
              color: LEVELOPTIONS.find((item) => item.id === levelId).color,
            }}
          />
          <MainText
            sx={{
              color:
                LEVELOPTIONS.find((item) => item.id === levelId).color +
                " !important",
              borderBottom: 1,
              borderColor: LEVELOPTIONS.find((item) => item.id === levelId)
                .color,
            }}
          >
            {LEVELOPTIONS.find((item) => item.id === levelId).name}
          </MainText>
        </CenteredRow>
        <Collapse
          in={levelListVisible}
          timeout={500}
          sx={{
            position: "absolute",
            top: width < 660 ? 180 : 200,
            left: width < 700 ? 80 : width < 1350 ? 100 : 130,
            background: Theme.palette.card,
            border: 1,
            borderColor: Theme.palette.border,
            borderRadius: 3,
            padding: 1,
          }}
        >
          <Stack gap={2}>
            {LEVELOPTIONS.map((item) => (
              <Box
                key={item.id}
                onClick={() => handleLevelChange(item.id)}
                sx={{
                  cursor: "pointer",
                  borderBottom: 1,
                  borderColor: Theme.palette.border,
                }}
              >
                <MainText sx={{ color: item.color + " !important" }}>
                  {item.icon}&nbsp;
                  {item.name}
                </MainText>
              </Box>
            ))}
          </Stack>
        </Collapse>
        {!isTypesPending && (
          <Stack direction={"row"} gap={1} flexWrap={"wrap"}>
            <AllCenteredColumn
              onClick={() => setTypeId(-1)}
              sx={{
                cursor: "pointer",
                background:
                  typeId === -1
                    ? `${Theme.palette.blue}22`
                    : Theme.palette.card,
                border: 1,
                borderRadius: 3,
                borderColor:
                  typeId === -1
                    ? `${Theme.palette.blue}55`
                    : Theme.palette.border,
                p: 1,
                transition: "300ms",
                "&:hover": {
                  transform: "translateY(-2px)",
                },
              }}
            >
              <SecondaryTitle
                sx={{
                  color: -1 === typeId && Theme.palette.blue + " !important",
                }}
              >
                All
              </SecondaryTitle>
            </AllCenteredColumn>
            {types.map((item) => {
              const icon = WORKOUT_TYPES_DATA.find(
                (type) => type.id === item.id,
              ).icon;
              return (
                <AllCenteredColumn
                  onClick={() => setTypeId(item.id)}
                  sx={{
                    cursor: "pointer",
                    background:
                      item.id === typeId
                        ? `${item.color}22`
                        : Theme.palette.card,
                    border: 1,
                    borderRadius: 3,
                    borderColor:
                      item.id === typeId
                        ? `${item.color}55`
                        : Theme.palette.border,
                    p: 1,
                    transition: "300ms",
                    "&:hover": {
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <span
                    style={{ fontSize: "0.9rem", color: Theme.palette.text }}
                  >
                    {icon}
                  </span>
                  <SecondaryTitle
                    sx={{
                      color: item.id === typeId && item.color + " !important",
                      fontSize: "0.9rem",
                    }}
                  >
                    {item.name}
                  </SecondaryTitle>
                </AllCenteredColumn>
              );
            })}
          </Stack>
        )}
        {!categoriesPending && (
          <Stack direction={"row"} flexWrap={"wrap"} gap={1}>
            {categories.map((item) => {
              const icon = EXERCISE_CATEGORIES_DATA.find(
                (type) => type.id === item.id,
              ).icon;
              return (
                <CenteredRow
                  gap={1}
                  onClick={() => setCategoryId(item.id)}
                  sx={{
                    cursor: "pointer",
                    background:
                      item.id === categoryId
                        ? `${Theme.palette.orange}22`
                        : Theme.palette.card,
                    border: 1,
                    borderRadius: 3,
                    borderColor:
                      item.id === categoryId
                        ? Theme.palette.orange
                        : Theme.palette.border,
                    p: 1,
                    transition: "300ms",

                    "&:hover": {
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <span
                    style={{ fontSize: "0.9rem", color: Theme.palette.text }}
                  >
                    {icon}
                  </span>
                  <MainText
                    sx={{
                      color:
                        item.id === categoryId &&
                        Theme.palette.orange + " !important",
                      fontSize: "0.9rem",
                    }}
                  >
                    {item.name}
                  </MainText>
                </CenteredRow>
              );
            })}
          </Stack>
        )}
      </SpacedRow>
      {workoutContent}
    </Stack>
  );
};
export default UserWorkouts;
